/**
 * PasswordReset component module.
 * @module shared/pages/PasswordReset
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import {Tile, LoadingSpinner, DescriptiveText} from '../../shared';
import {useParams} from 'react-router-dom';
import useQueryString from '../utils/useQueryString';
import useQueryNumber from '../utils/useQueryNumber';
import Page from '../components/Page';
import CreatePassword from '../widgets/CreatePassword';
import Button from '../components/Button';
import Row from '../components/Row';
import axiosInstance from '../utils/axios';
import {_openModal} from '../reducers/modals';
import history from '../../history';
import useSWR from 'swr';
import {_closeSession, _resetPassword} from '../reducers/user';

/**
 * PasswordReset component
 * @name PasswordReset
 */
const PasswordReset = ({openModal, resetPassword, logout}) => {
  const {token} = useParams();
  const [email] = useQueryString('email');
  const [isSSO] = useQueryNumber('isSSO', 0);
  const [reset, setReset] = React.useState({});
  const [processing, setProcessing] = React.useState(false);
  const [product, setProduct] = React.useState(null);
  localStorage.setItem('email', email);
  localStorage.setItem('token', token);
  localStorage.setItem('initialLoad', JSON.stringify(true));

  const {data: role} = useSWR(
    ['user/role', token],
    () => axiosInstance.get('/user/role')
      .then(res => res.data)
      .catch(() => {
        openModal({
          type: 'MessageModal',
          title: 'An Issue Occurred',
          body: 'There was an issue with your request. Please try again.',
          button: {
            text: 'Resend Email',
            action: () => {
              resetPassword(email);
              logout();
            },
          },
        });
      })
  );

  const submit = e => {
    e.preventDefault();
    setProcessing(true);
    return Promise.resolve(isSSO && axiosInstance.get('/user/product').then(res => setProduct(res.data)))
      .then(function updatePassword() {
        return axiosInstance.put('/user/password', {password: {new: reset.password}})
          .then(function success() {
            localStorage.setItem('token', undefined);
            if (!isSSO) {
              history.push('/');
              setTimeout(() => openModal({
                type: 'MessageModal',
                title: 'Password Saved',
                body: 'Your password has been saved successfully.',
              }), 250);
            }
          });
      })
      .catch(err => openModal({type: 'Error', title: 'Failed to Set Password', err}))
      .finally(() => setProcessing(false));
  };

  if (!role) {
    return <LoadingSpinner/>;
  }
  return <Page center='true'>
    <Tile title='Password Reset' width='600px'>
      {!product || processing
        ? <form id={'resetPassword'} name={'resetPassword'} onSubmit={submit}>
          <CreatePassword onChange={(name, value) => setReset(c => ({...c, [name]: value}))} dataPoint={reset} role={role}/>
          <Row>
            <Button label={'Submit'} importance={'primary'} size={'md'} action={'SUBMIT'} processing={processing}/>
          </Row>
        </form>
        : <DescriptiveText>
          Your password was successfully updated. Please return to {product.ssoPartnerDisplayName} to login.
        </DescriptiveText>}
    </Tile>
  </Page>;
};

const mapDispatchToProps = dispatch => ({
  openModal: modal => dispatch(_openModal(modal)),
  resetPassword: email => dispatch(_resetPassword(email)),
  logout: () => dispatch(_closeSession()),
});

export default connect(null, mapDispatchToProps)(PasswordReset);

PasswordReset.propTypes = {
  openModal: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
};
